import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'lodash/isEqual';

import { assertInstanceOf } from 'common/assertions';
import { ColorPicker } from 'common/components/ColorPicker';

import Constants from 'lib/Constants';
import { StorytellerReduxStoreFactory } from '../store/StorytellerReduxStore';
import { selectRTEActiveFormats } from '../store/reducers/RichTextEditorReducer';

interface Format {
  id: string;
  tag: string | null;
  name: string;
  dropdown: boolean;
  group?: number;
  panel?: boolean;
  color?: string;
}

export interface FormatController {
  execute(command: string, color?: string): void;
}

interface ButtonOptions {
  class: string;
  'data-editor-action': 'toggle-panel' | 'toggle-format';
  'data-label': string;
  'data-editor-command'?: string;
  'aria-label'?: string;
}

/**
 * The Formats configuration block is used by the RichTextEditorToolbar and
 * the RichTextEditorFormatController to specify which format options are
 * supported, how they should be displayed on the toolbar and what action
 * should be taken when they are executed.
 *
 * @constructor
 * @param {jQuery} element
 * @param {object[]} formats
 *   @property {string} id - The internal name of the operation that is
 *     associated with this format.
 *   @property {string} tag
 *   @property {string} name - The human-readable name that will appear
 *     as a tool-tip if the user hovers the cursor over the option or button.
 *   @property {boolean} dropdown - Whether or not this format should appear
 *     as an option in the block format select control. False indicates that
 *     the format should appear as a button.
 *   @property {number} [group] - The button group in which this format's
 *     button should appear.
 */
export const richTextEditorToolbar = RichTextEditorToolbar(
  '#rich-text-editor-toolbar',
  Constants.RICH_TEXT_FORMATS,
);

export default function RichTextEditorToolbar(selector: string, formats: Format[]) {
  assertInstanceOf(formats, Array);

  let element: JQuery<HTMLElement>;
  let formatController: FormatController | null = null;
  const colorPickerId = 'rich-text-editor-color-picker';

  function setupReduxSubscription() {
    const store = StorytellerReduxStoreFactory();
    let previousActiveFormats: Format[] = [];

    return store.subscribe(() => {
      const currentActiveFormats = selectRTEActiveFormats(store.getState());
      if (!isEqual(currentActiveFormats, previousActiveFormats)) {
        updateActiveFormats(currentActiveFormats);
        previousActiveFormats = [...currentActiveFormats];
      }
    });
  }

  $(function () {
    element = $(selector);
    createToolbar();
    showToolbar();

    setupReduxSubscription();
  });

  /**
   * Public methods
   */

  const link = function (editorFormatController: FormatController) {
    formatController = editorFormatController;

    element.removeClass('dim').addClass('active');
    element.find('.rich-text-editor-toolbar-select').prop('disabled', false);
  };

  const unlink = function () {
    formatController = null;
    element.removeClass('active').addClass('dim');
    element.find('.rich-text-editor-toolbar-select').prop('disabled', true);
  };

  const destroy = function () {
    detachEventListeners();
    element.remove();
  };

  return {
    link,
    unlink,
    destroy,
  };

  /**
   * Private methods
   */

  function attachEventListeners() {
    element.on('change', '[data-editor-action="change-format"]', handleToolbarSelectChange);

    element.on('click', '[data-editor-action="toggle-format"]', handleToolbarButtonClick);

    element.on('click', '[data-editor-action="toggle-panel"]', handleToolbarPanelToggle);

    element.on('click', '[data-editor-command="textColor"]', handleColorSwatchClick);
  }

  function detachEventListeners() {
    element.off('change', '[data-editor-action="change-format"]', handleToolbarSelectChange);

    element.off('click', '[data-editor-action="toggle-format"]', handleToolbarButtonClick);

    element.off('click', '[data-editor-action="toggle-panel"]', handleToolbarPanelToggle);

    element.off('click', '[data-editor-command="textColor"]', handleColorSwatchClick);
  }

  function renderSelect(selectFormats: Format[]) {
    const selectFormatElements: JQuery<HTMLElement>[] = [];
    let option: JQuery<HTMLElement>;

    for (let i = 0; i < selectFormats.length; i++) {
      option = $('<option>', { value: selectFormats[i].id });
      option.text(selectFormats[i].name);

      if (selectFormats[i].tag === null) {
        option.prop('selected', true);
      }

      selectFormatElements.push(option);
    }

    return $('<div>', { class: 'rich-text-editor-toolbar-select-group' }).append(
      $('<div>', { class: 'rich-text-editor-toolbar-select-container' }).append([
        $('<select>', {
          class: 'rich-text-editor-toolbar-select',
          disabled: true,
          'aria-label': 'select text heading level',
          name: 'select text heading level',
          'data-editor-action': 'change-format',
        }).append(selectFormatElements),
        $('<div>', { class: 'rich-text-editor-toolbar-select-hint' }),
      ]),
    );
  }

  function renderColorPickerPanel() {
    const $panel = $('<div>', { class: 'rich-text-editor-toolbar-text-color-panel color-picker-panel' });
    return $panel;
  }

  function renderButtonGroup(group: Format[]) {
    const toolbarButtons: JQuery<HTMLElement>[] = [];
    let buttonClass: string;
    let buttonOptions: ButtonOptions;
    let $button: JQuery<HTMLElement>;

    toolbarButtons.push($('<span>', { class: 'rich-text-editor-toolbar-divider' }));

    for (let i = 0; i < group.length; i++) {
      buttonClass = `rich-text-editor-toolbar-btn rich-text-editor-toolbar-btn-${group[i].id}`;
      buttonOptions = {
        class: buttonClass,
        'data-editor-action': group[i].panel ? 'toggle-panel' : 'toggle-format',
        'data-label': group[i].name,
        'aria-label': group[i].name,
      };

      if (!group[i].panel) {
        buttonOptions['data-editor-command'] = group[i].id;
      }

      $button = $('<button>', buttonOptions);

      if (group[i].panel === true) {
        switch (group[i].id) {
          case 'textColor':
            $button.append([
              $('<div>', {
                class: 'rich-text-editor-toolbar-text-color-swatch',
              }),
            ]);
            toolbarButtons.push(
              $('<div>', {
                class: 'rich-text-editor-toolbar-btn-panel-group',
              }).append([$button, renderColorPickerPanel()]),
            );
            break;

          default:
            break;
        }
      } else {
        toolbarButtons.push($button);
      }
    }

    const isTextColor = toolbarButtons.some(function (toolbarButton) {
      return toolbarButton.find('.rich-text-editor-toolbar-btn-textColor').length > 0;
    });

    const className = isTextColor
      ? 'rich-text-editor-toolbar-btn-group rich-text-editor-toolbar-btn-group-with-block-elements'
      : 'rich-text-editor-toolbar-btn-group';

    return $('<div>', { class: className }).append(toolbarButtons);
  }

  function createToolbar() {
    const dropdownFormats = formats.filter(function (format) {
      return format.dropdown === true;
    });
    const buttonFormats = formats.filter(function (format) {
      return format.dropdown === false;
    });
    const buttonGroups: Format[][] = [];
    const controls: JQuery<HTMLElement>[] = [];

    // Split buttons into button groups.
    buttonFormats.forEach(function (format) {
      if (format.group !== undefined && format.group >= buttonGroups.length) {
        buttonGroups.push([]);
      }

      if (format.group !== undefined) {
        buttonGroups[format.group].push(format);
      }
    });

    // Render the button groups and the format select.
    if (buttonGroups.length > 0) {
      controls.push(renderButtonGroup(buttonGroups[0]));
    }

    controls.push(renderSelect(dropdownFormats));

    for (let i = 1; i < buttonGroups.length; i++) {
      controls.push(renderButtonGroup(buttonGroups[i]));
    }

    element.append(controls);
    element.addClass('dim');

    // Finally, set up events and add the toolbar to the container.
    attachEventListeners();
  }

  function rgbToHex(rgb: string): string {
    const rgbValues = rgb.substr(4).split(')')[0].split(',');
    const hex = rgbValues
      // Convert each value to base 16 (hex) integer
      .map((x) => parseInt(x).toString(16))
      // Pad single digit numbers with a zero
      .map((value) => (value.length === 1 ? '0' + value : value))
      .join('');

    return '#' + hex;
  }

  function renderColorPicker(color: string = '') {
    const button = element.find('.rich-text-editor-toolbar-text-color-panel');

    // When toolbar is unlinked we pass an empty string to render transparent swatch.
    // Format controller returns color in rgb format. We convert it to hex
    // before passing it to ColorPicker so that the input container renders the color
    // as hex.
    const colorPickerProps = {
      handleColorChange,
      value: color.length ? rgbToHex(color) : '',
      id: colorPickerId,
      hideColorFrame: true,
    };
    return ReactDOM.render(<ColorPicker {...colorPickerProps} />, button[0]);
  }

  function showToolbar() {
    element.addClass('visible');
  }

  function updateActiveFormats(activeFormats: Format[]) {
    const selectElement = element.find('.rich-text-editor-toolbar-select');
    element.find('.rich-text-editor-toolbar-btn').removeClass('active');

    if (activeFormats.length === 0) {
      selectElement.val('text');
    } else {
      selectElement.val('text');

      for (let i = 0; i < activeFormats.length; i++) {
        const thisFormat = activeFormats[i];

        if (thisFormat.dropdown === true) {
          selectElement.val(thisFormat.id);
        } else if (thisFormat.id === 'textColor') {
          if (!element.hasClass('dim')) {
            renderColorPicker(thisFormat.color || '');
            element
              .find('.rich-text-editor-toolbar-text-color-swatch')
              .css('background-color', thisFormat.color || '');
          }
        } else {
          element.find(`.rich-text-editor-toolbar-btn-${thisFormat.id}`).addClass('active');
        }
      }
    }
  }

  function handleToolbarSelectChange(e: JQuery.ChangeEvent) {
    const command = (e.target as HTMLSelectElement).value;

    if (formatController !== null) {
      formatController.execute(command);
    }
  }

  function handleToolbarButtonClick(e: JQuery.ClickEvent) {
    const command = (e.target as HTMLElement).getAttribute('data-editor-command');

    if (formatController !== null && command) {
      formatController.execute(command);
    }
  }

  function handleToolbarPanelToggle(e: JQuery.ClickEvent) {
    if ($('#rich-text-editor-toolbar').hasClass('dim')) {
      return;
    }

    // We only want to trigger this on the parent button, not any
    // of its children (which will also trigger this event handler
    // when clicked).
    if ($(e.target).hasClass('rich-text-editor-toolbar-btn-textColor')) {
      $(e.target).toggleClass('active');
      element.find(`#${colorPickerId}-color-frame`).trigger('click');
    } else if ($(e.target).hasClass('selected-color-frame')) {
      $('.rich-text-editor-toolbar-btn-textColor').addClass('active');
    }
  }

  function handleColorSwatchClick(e: JQuery.ClickEvent) {
    const color = (e.target as HTMLElement).getAttribute('data-text-color');
    if (color) {
      handleColorChange(color);
    }
  }

  function handleColorChange(color: string) {
    const command = 'textColor';

    if (formatController !== null) {
      formatController.execute(command, color);
    }
  }
}
